import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

const PinPad = ({ onSuccess, onClose, isFirstTime = false, t }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [confirmPin, setConfirmPin] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  const handleNumberClick = (num) => {
    if (isFirstTime) {
      if (isConfirming) {
        if (confirmPin.length < 4) {
          setConfirmPin(confirmPin + num);
        }
      } else {
        if (pin.length < 4) {
          setPin(pin + num);
        }
      }
    } else {
      if (pin.length < 4) {
        setPin(pin + num);
      }
    }
  };

  const handleDelete = () => {
    if (isConfirming) {
      setConfirmPin(confirmPin.slice(0, -1));
    } else {
      setPin(pin.slice(0, -1));
    }
  };

  useEffect(() => {
    if (isFirstTime) {
      if (!isConfirming && pin.length === 4) {
        setIsConfirming(true);
      }
      if (isConfirming && confirmPin.length === 4) {
        if (pin === confirmPin) {
          onSuccess(pin);
        } else {
          setError(true);
          setTimeout(() => {
            setError(false);
            setPin('');
            setConfirmPin('');
            setIsConfirming(false);
          }, 500);
        }
      }
    } else {
      if (pin.length === 4) {
        onSuccess(pin);
      }
    }
  }, [pin, confirmPin, isConfirming, isFirstTime, onSuccess]);

  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className={`bg-white rounded-3xl p-8 max-w-md w-full relative ${
          error ? 'animate-shake' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
        data-testid="pinpad-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          data-testid="pinpad-close-button"
        >
          <X size={24} />
        </button>

        <h2 className="text-2xl font-bold text-center mb-2 fredoka">
          {isFirstTime
            ? isConfirming
              ? 'Confirm Passcode'
              : t('createPasscode')
            : t('enterPasscode')}
        </h2>

        <div className="flex justify-center gap-3 mb-8" data-testid="pin-display">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-bold ${
                (isConfirming ? confirmPin : pin).length > i
                  ? 'bg-[#FFD700] text-gray-800'
                  : 'bg-gray-100 text-gray-300'
              }`}
            >
              {(isConfirming ? confirmPin : pin).length > i ? '●' : ''}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          {numbers.map((num) => (
            <motion.button
              key={num}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleNumberClick(num.toString())}
              className="h-16 rounded-2xl bg-slate-100 hover:bg-slate-200 text-2xl font-bold fredoka transition-colors"
              data-testid={`pin-button-${num}`}
            >
              {num}
            </motion.button>
          ))}
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleDelete}
          className="w-full h-16 rounded-2xl bg-red-100 hover:bg-red-200 text-red-600 font-bold fredoka transition-colors"
          data-testid="pin-delete-button"
        >
          Delete
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default PinPad;
